import { warehouseApi, type ApiStorageUnit, type ApiWarehouse } from '@/api/warehouseApi';
import { getProductsWithSizes } from '@/services/products';
import type { FilterRequest } from '@/types/filter';
import type { BarcodeLabelElement, CatalogElement, DesignerProduct, LabelElement, LabelPreviewData, LabelTemplate, ProductSizeVariant } from '@/types/label-template';
import type { WbProduct } from '@/types/product';
import { snapDataMatrixCodeSizeMm } from '@/utils/datamatrix-size';
import { getDataMatrixTestTemplate } from '@/utils/datamatrix-test-template';
import { DESIGNER_PREVIEW_DEFAULT_LABEL, fillEmptyLabelFields } from '@/utils/designer-preview-defaults';
import {
  defaultPreviewTestOverrides,
  loadPreviewTestStorage,
  savePreviewTestStorage,
  type PreviewTestOverrides,
} from '@/utils/designer-preview-test-data';
import { renderFastwmsTemplate, validateFastwmsTemplate } from '@/utils/fastwms-variables';
import { toLabelElement } from '@/utils/label-element-model';
import {
  buildImageSvgMarkup,
  buildShapeSvgMarkup,
  clampDecorativeStrokeWidth,
  DEFAULT_DECORATIVE_STROKE_WIDTH,
  SHAPE_DEFAULT_MM,
  SHAPE_LABELS,
  type ShapeGraphicKind,
} from '@/utils/shape-svgs';
import {
  createDefaultTemplate,
  createElement,
  DEFAULT_PRINT_PROFILE_ID,
  getTemplateValueByBinding,
  PRINT_PROFILES,
  STATIC_TEXT_FIELD_LABEL,
} from '@/utils/template-helpers';
import { computed, reactive, watch } from 'vue';

export type DesignerFieldTabKey = 'product' | 'codes' | 'signs';

/** Режим данных превью: товарный каталог или ячейка склада (QR / адрес). */
export type LabelDesignerContext = 'product' | 'warehouse';

interface DesignerState {
  currentTemplate: LabelTemplate;
  selectedElementId: string | null;
  selectedElementIds: string[];
  zoom: number;
  printProfileId: string;
  printDensity: number;
  /** DPI при активном пресете или для режима custom (последний выбранный DPI). */
  lastPrintDpi: 203 | 300;
  safeMarginMm: number;
  /** Пунктир «безопасная зона» и ограничение элементов от края холста. */
  safeMarginEnabled: boolean;
  /** Зеркальный режим холста для редактирования (только визуально). */
  canvasMirrored: boolean;
  showGrid: boolean;
  snapEnabled: boolean;
  snapStepMm: number;
  previewMode: boolean;
  selectedProductId: string;
  selectedSizeBarcode: string;
  products: DesignerProduct[];
  selectedProduct: DesignerProduct | null;
  selectedSize: ProductSizeVariant | null;
  previewData: LabelPreviewData;
  /** Вкладка каталога полей (Текст / Коды / Символы) — синхронизируется между левой панелью и нижней панелью. */
  activeFieldTab: DesignerFieldTabKey;
  /** Источник данных для привязок label.* на холсте. */
  labelContext: LabelDesignerContext;
  /** Склады и единицы хранения для превью «Ячейка» (загрузка с бэкенда). */
  cellPreviewWarehouses: ApiWarehouse[];
  cellPreviewUnitsAll: ApiStorageUnit[];
  selectedCellWarehouseId: string | null;
  selectedCellUnitId: string | null;
  cellPreviewLoadStatus: 'idle' | 'loading' | 'ok' | 'error';
  /** Подмена полей превью вручную (редактор без реальных КИЗ/товаров). */
  previewTestEnabled: boolean;
  previewTestOverrides: PreviewTestOverrides;
  /** Id сохранённого шаблона на сервере (для «Сохранить» после перезагрузки страницы). */
  activeTemplateId: string | null;
}

interface DesignerSnapshot {
  currentTemplate: LabelTemplate;
  selectedElementId: string | null;
  selectedElementIds: string[];
  selectedProductId: string;
  selectedSizeBarcode: string;
  labelContext: LabelDesignerContext;
  selectedCellWarehouseId: string | null;
  selectedCellUnitId: string | null;
}

const HISTORY_LIMIT = 100;

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function pxToMmAtDpi(px: number, dpi: 203 | 300): number {
  return (px * 25.4) / dpi;
}

function sameSnapshot(a: DesignerSnapshot | null, b: DesignerSnapshot | null): boolean {
  if (!a || !b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}


function emptyPreviewData(): LabelPreviewData {
  return {
    label: { ...DESIGNER_PREVIEW_DEFAULT_LABEL },
  };
}

function applyPreviewTestLayer(data: LabelPreviewData): LabelPreviewData {
  if (!state.previewTestEnabled) return data;
  const overrides = state.previewTestOverrides;
  const label = { ...data.label };
  for (const [key, value] of Object.entries(overrides)) {
    if (value == null) continue;
    const trimmed = String(value).trim();
    if (!trimmed) continue;
    label[key as keyof LabelPreviewData['label']] = trimmed;
  }
  return { ...data, label };
}

function finalizePreviewData(data: LabelPreviewData): LabelPreviewData {
  const withDefaults: LabelPreviewData = {
    ...data,
    label: fillEmptyLabelFields(data.label),
  };
  return applyPreviewTestLayer(withDefaults);
}

function createProductPreviewData(product: DesignerProduct | null, productSize: ProductSizeVariant | null): LabelPreviewData {
  const userId = typeof localStorage !== 'undefined' ? localStorage.getItem('user_id') : null;
  if (!product || !productSize) return emptyPreviewData();
  const size = (productSize.value || '').trim();
  const techSize = (productSize.techSize || productSize.value || '').trim();
  const shortAddr = (product.shortAddress ?? '').trim();
  const label = {
    ...emptyPreviewData().label,
    name: product.name,
    brand: product.brand,
    article: product.article,
    sellerArticle: product.sellerArticle,
    color: product.color,
    size: size,
    techSize,
    barcode: productSize.barcode,
    composition: product.composition,
    sellerShortName: product.sellerShortName || product.sellerName,
    sellerName: product.sellerName,
    shortAddress: shortAddr,
    address: (product.address ?? shortAddr).trim() || DESIGNER_PREVIEW_DEFAULT_LABEL.address,
    userId: userId ? String(userId) : DESIGNER_PREVIEW_DEFAULT_LABEL.userId,
  };
  return {
    label,
    product: {
      name: label.name,
      sellerName: label.sellerName,
      sellerShortName: label.sellerShortName,
      userId: label.userId,
      article: label.article,
      sellerArticle: label.sellerArticle,
      barcode: label.barcode,
      size: label.size,
      techSize: label.techSize,
      sizeCombined: `${label.size}-${label.techSize}`,
      color: label.color,
      brand: label.brand,
      composition: label.composition,
      country: label.country,
      orderNumber: label.number,
      date: label.manufactureDate,
      kiz: label.serial,
    },
  };
}

function mergeElementDefaults(element: LabelElement | Record<string, unknown>): LabelElement {
  return toLabelElement({ ...(element as object) } as Record<string, unknown>);
}

function computeBarcodeContainerHeightMm(element: BarcodeLabelElement, dpi: 203 | 300): number {
  const textGapPx = 10;
  const textHeightPx = Math.ceil(
    Math.max(1, element.barcodeTextFontSize) * Math.max(1, element.barcodeTextLineHeight ?? 1.2),
  );
  const reservedTextMm = pxToMmAtDpi(textGapPx + textHeightPx, dpi);
  const nextHeight = element.barcodePaddingTop
    + element.barcodeHeight
    + element.barcodePaddingBottom
    + reservedTextMm;
  return Math.max(2, Math.round((nextHeight + Number.EPSILON) * 10) / 10);
}

export const STORAGE_KEY = 'label-designer.state';

const defaultPrintProfile =
  PRINT_PROFILES.find((p) => p.id === DEFAULT_PRINT_PROFILE_ID) ?? PRINT_PROFILES[0];

const state = reactive<DesignerState>({
  currentTemplate: createDefaultTemplate(),
  selectedElementId: null,
  selectedElementIds: [],
  zoom: 1,
  printProfileId: defaultPrintProfile.id,
  printDensity: defaultPrintProfile.density,
  lastPrintDpi: defaultPrintProfile.dpi,
  safeMarginMm: defaultPrintProfile.safeMarginMm,
  safeMarginEnabled: true,
  canvasMirrored: false,
  showGrid: true,
  snapEnabled: true,
  snapStepMm: 1,
  previewMode: false,
  selectedProductId: '',
  selectedSizeBarcode: '',
  products: [],
  selectedProduct: null,
  selectedSize: null,
  previewData: emptyPreviewData(),
  activeFieldTab: 'product',
  labelContext: 'product',
  cellPreviewWarehouses: [],
  cellPreviewUnitsAll: [],
  selectedCellWarehouseId: null,
  selectedCellUnitId: null,
  cellPreviewLoadStatus: 'idle',
  previewTestEnabled: false,
  previewTestOverrides: {},
  activeTemplateId: null,
});

function buildWarehouseCellPreviewData(): LabelPreviewData {
  const warehouse =
    state.cellPreviewWarehouses.find((w) => w.id === state.selectedCellWarehouseId)
    ?? state.cellPreviewWarehouses[0]
    ?? null;
  const units = warehouse
    ? state.cellPreviewUnitsAll.filter((u) => u.warehouseId === warehouse.id)
    : [];
  const unit = units.find((u) => u.id === state.selectedCellUnitId) ?? units[0] ?? null;
  if (!warehouse || !unit) {
    return emptyPreviewData();
  }

  /** Полная локация (человекочитаемый адрес ячейки) — основа для QR с привязкой label.number */
  const fullLocation = (unit.address?.trim() || unit.locationCode?.trim() || '—') as string;
  const addressLatin = (unit.addressLatin?.trim() || unit.locationCode?.trim() || fullLocation) as string;
  const whCode = warehouse.code || 'MAIN';
  const qrPayload = `FASTWMS|WH:${whCode}|${addressLatin}`;

  return {
    label: {
      ...emptyPreviewData().label,
      name: unit.externalCode ? `Ячейка · ${unit.externalCode}` : `Ячейка ${fullLocation}`,
      brand: warehouse.name,
      article: warehouse.code,
      sellerArticle: unit.externalCode || fullLocation,
      color: fullLocation,
      size: addressLatin,
      techSize: addressLatin,
      gtin: '04600000000001',
      serial: unit.externalCode || 'CELL',
      barcode: qrPayload,
      number: fullLocation,
      composition: `Единица хранения ${unit.externalCode} · ${fullLocation}`,
      country: 'RU',
      manufacturer: 'FASTWMS',
      manufactureDate: new Date().toLocaleDateString('ru-RU'),
      sellerName: '—',
      sellerShortName: '—',
      userId: warehouse.code,
      shortAddress: fullLocation,
      addressLatin,
      storageGroup: '—',
    },
    product: {
      name: unit.externalCode ? `${unit.externalCode} · ${fullLocation}` : `Ячейка ${fullLocation}`,
      sellerName: warehouse.name,
      article: warehouse.code,
      sellerArticle: unit.externalCode,
      barcode: qrPayload,
      size: addressLatin,
      techSize: fullLocation,
      sizeCombined: `${addressLatin} / ${fullLocation}`,
      color: unit.type,
      brand: warehouse.code,
      composition: `Адрес: ${fullLocation}`,
      country: 'RU',
      orderNumber: fullLocation,
      date: new Date().toLocaleDateString('ru-RU'),
      kiz: unit.externalCode,
    },
  };
}

function refreshPreviewData() {
  if (state.labelContext === 'warehouse') {
    state.previewData = finalizePreviewData(buildWarehouseCellPreviewData());
  } else {
    state.previewData = finalizePreviewData(
      createProductPreviewData(state.selectedProduct, state.selectedSize),
    );
  }
  syncElementValues();
}

function persistPreviewTestStorage() {
  savePreviewTestStorage({
    enabled: state.previewTestEnabled,
    overrides: state.previewTestOverrides,
  });
}

function setPreviewTestEnabled(enabled: boolean) {
  state.previewTestEnabled = enabled;
  persistPreviewTestStorage();
  refreshPreviewData();
}

function setPreviewTestOverrides(overrides: PreviewTestOverrides) {
  state.previewTestOverrides = { ...overrides };
  persistPreviewTestStorage();
  refreshPreviewData();
}

function fillPreviewTestDefaults() {
  state.previewTestOverrides = defaultPreviewTestOverrides();
  state.previewTestEnabled = true;
  persistPreviewTestStorage();
  refreshPreviewData();
}

function resetPreviewTestOverrides() {
  state.previewTestOverrides = {};
  persistPreviewTestStorage();
  refreshPreviewData();
}

const initialPreviewTest = loadPreviewTestStorage();
state.previewTestEnabled = initialPreviewTest.enabled;
state.previewTestOverrides = initialPreviewTest.overrides;
refreshPreviewData();

async function loadCellPreviewData() {
  if (state.cellPreviewLoadStatus === 'loading') return;
  state.cellPreviewLoadStatus = 'loading';
  try {
    const [warehouses, units] = await Promise.all([
      warehouseApi.listWarehouses(),
      warehouseApi.listStorageUnits(),
    ]);
    state.cellPreviewWarehouses = warehouses;
    state.cellPreviewUnitsAll = units;

    let wId = state.selectedCellWarehouseId;
    if (!wId || !warehouses.some((w) => w.id === wId)) {
      wId = warehouses[0]?.id ?? null;
    }
    state.selectedCellWarehouseId = wId;

    const forWh = wId ? units.filter((u) => u.warehouseId === wId) : [];
    let uId = state.selectedCellUnitId;
    if (!uId || !forWh.some((u) => u.id === uId)) {
      uId = forWh[0]?.id ?? null;
    }
    state.selectedCellUnitId = uId;

    state.cellPreviewLoadStatus = 'ok';
  } catch {
    state.cellPreviewLoadStatus = 'error';
  }
  if (state.labelContext === 'warehouse') {
    refreshPreviewData();
  }
}

const selectedElement = computed(() =>
  state.currentTemplate.elements.find((el) => el.id === state.selectedElementId) || null,
);
const selectedElements = computed(() =>
  state.currentTemplate.elements.filter((el) => state.selectedElementIds.includes(el.id)),
);

function getEffectiveDpi(): 203 | 300 {
  const profile = PRINT_PROFILES.find((p) => p.id === state.printProfileId);
  return profile ? profile.dpi : state.lastPrintDpi;
}

function syncTemplateDpi() {
  state.currentTemplate.dpi = getEffectiveDpi();
}

function applyTemplateDpi(dpi: 203 | 300) {
  const preset = PRINT_PROFILES.find((p) => p.dpi === dpi);
  if (preset) {
    setPrintProfile(preset.id);
  } else {
    state.printProfileId = 'custom';
    state.lastPrintDpi = dpi;
  }
  state.currentTemplate.dpi = dpi;
}

const effectiveDpi = computed(() => getEffectiveDpi());

/** Единицы хранения выбранного склада (для списка в модалке «Ячейка»). */
const cellPreviewUnitsForSelectedWarehouse = computed((): ApiStorageUnit[] => {
  const warehouse =
    state.cellPreviewWarehouses.find((w) => w.id === state.selectedCellWarehouseId)
    ?? state.cellPreviewWarehouses[0]
    ?? null;
  if (!warehouse) return [];
  return state.cellPreviewUnitsAll.filter((u) => u.warehouseId === warehouse.id);
});

const undoStack: DesignerSnapshot[] = [];
const redoStack: DesignerSnapshot[] = [];
const historyDepth = reactive({ undo: 0, redo: 0 });
let isTimeTraveling = false;
const canUndo = computed(() => historyDepth.undo > 0);
const canRedo = computed(() => historyDepth.redo > 0);

function createSnapshot(): DesignerSnapshot {
  return {
    currentTemplate: deepClone(state.currentTemplate),
    selectedElementId: state.selectedElementId,
    selectedElementIds: [...state.selectedElementIds],
    selectedProductId: state.selectedProductId,
    selectedSizeBarcode: state.selectedSizeBarcode,
    labelContext: state.labelContext,
    selectedCellWarehouseId: state.selectedCellWarehouseId,
    selectedCellUnitId: state.selectedCellUnitId,
  };
}

function applySnapshot(snapshot: DesignerSnapshot) {
  state.currentTemplate = {
    ...snapshot.currentTemplate,
    elements: snapshot.currentTemplate.elements.map((element) => mergeElementDefaults(element)),
  };
  state.selectedElementId = snapshot.selectedElementId;
  state.selectedElementIds = [...snapshot.selectedElementIds];
  state.selectedProductId = snapshot.selectedProductId;
  const product = state.products.find((item) => item.id === snapshot.selectedProductId) ?? null;
  state.selectedProduct = product;
  const size = product?.sizes.find((item) => item.barcode === snapshot.selectedSizeBarcode) ?? product?.sizes[0] ?? null;
  state.selectedSizeBarcode = size?.barcode ?? '';
  state.selectedSize = size;
  state.labelContext = snapshot.labelContext ?? 'product';
  state.selectedCellWarehouseId = snapshot.selectedCellWarehouseId ?? null;
  state.selectedCellUnitId = snapshot.selectedCellUnitId ?? null;
  refreshPreviewData();
}

function withHistory(mutator: () => void) {
  if (isTimeTraveling) {
    mutator();
    return;
  }
  const before = createSnapshot();
  mutator();
  const after = createSnapshot();
  if (sameSnapshot(before, after)) return;
  undoStack.push(before);
  if (undoStack.length > HISTORY_LIMIT) undoStack.shift();
  redoStack.length = 0;
  historyDepth.undo = undoStack.length;
  historyDepth.redo = redoStack.length;
}

function undo() {
  if (!undoStack.length) return;
  const prev = undoStack.pop();
  if (!prev) return;
  const current = createSnapshot();
  isTimeTraveling = true;
  applySnapshot(prev);
  isTimeTraveling = false;
  redoStack.push(current);
  historyDepth.undo = undoStack.length;
  historyDepth.redo = redoStack.length;
}

function redo() {
  if (!redoStack.length) return;
  const next = redoStack.pop();
  if (!next) return;
  const current = createSnapshot();
  isTimeTraveling = true;
  applySnapshot(next);
  isTimeTraveling = false;
  undoStack.push(current);
  historyDepth.undo = undoStack.length;
  historyDepth.redo = redoStack.length;
}

function setPaperType(paperType: LabelTemplate['paperType']) {
  state.currentTemplate.paperType = paperType;
}

function setLabelSize(widthMm: number, heightMm: number) {
  withHistory(() => {
    state.currentTemplate.labelSize.widthMm = widthMm;
    state.currentTemplate.labelSize.heightMm = heightMm;
    state.currentTemplate.name = `Этикетка ${widthMm}x${heightMm}`;
  });
}

function addElement(seed: Parameters<typeof createElement>[0]) {
  withHistory(() => {
    if (seed.type !== 'svg') {
      const alreadyExists = state.currentTemplate.elements.some(
        (element) => element.type === seed.type && element.binding === seed.binding,
      );
      if (alreadyExists) return;
    }

    const value = seed.type === 'datamatrix'
      ? getDataMatrixTestTemplate()
      : getTemplateValueByBinding(seed.binding, seed.type);
    const layoutPreview =
      seed.type === 'text' && seed.binding === 'label.name'
        ? renderFastwmsTemplate(value, state.previewData)
        : undefined;
    const element = createElement(seed, value, layoutPreview);
    state.currentTemplate.elements.push(element);
    state.selectedElementId = element.id;
    state.selectedElementIds = [element.id];
  });
}

function addStaticText(initialText = 'Текст') {
  withHistory(() => {
    const text = initialText.trim() || 'Текст';
    const seed = {
      label: STATIC_TEXT_FIELD_LABEL,
      type: 'text' as const,
      binding: '',
      width: 40,
      height: 6,
      showLabel: false,
    };
    const element = createElement(seed, text);
    state.currentTemplate.elements.push(element);
    state.selectedElementId = element.id;
    state.selectedElementIds = [element.id];
  });
}

function addCatalogElement(item: CatalogElement) {
  withHistory(() => {
    const element = createElement(
      {
        label: item.name,
        type: 'svg',
        binding: '',
        width: 16,
        height: 16,
      },
      '',
    );
    const size = 16;
    const patch = {
      value: item.name,
      width: size,
      height: size,
      catalogElementId: item.id,
      svgCode: item.svgCode,
      svgVisible: true,
      svgPaddingTop: 0,
      svgPaddingLeft: 0,
      svgPaddingBottom: 0,
      svgPaddingRight: 0,
      svgAlign: 'left',
    };
    state.currentTemplate.elements.push(
      toLabelElement({ ...(element as object), ...patch } as Record<string, unknown>),
    );
    state.selectedElementId = element.id;
    state.selectedElementIds = [element.id];
  });
}

function addShapeGraphic(kind: ShapeGraphicKind) {
  withHistory(() => {
    const size = SHAPE_DEFAULT_MM[kind];
    const base = createElement(
      {
        label: SHAPE_LABELS[kind],
        type: 'svg',
        binding: '',
        width: size.width,
        height: size.height,
      },
      '',
    );
    state.currentTemplate.elements.push(
      toLabelElement({
        ...base,
        decorativeShapeKind: kind,
        decorativeStrokeWidth: DEFAULT_DECORATIVE_STROKE_WIDTH,
        svgCode: buildShapeSvgMarkup(kind, DEFAULT_DECORATIVE_STROKE_WIDTH),
        svgVisible: true,
        svgPaddingTop: 0,
        svgPaddingLeft: 0,
        svgPaddingBottom: 0,
        svgPaddingRight: 0,
        svgAlign: 'left',
      } as Record<string, unknown>),
    );
    state.selectedElementId = element.id;
    state.selectedElementIds = [element.id];
  });
}

function addImageFromDataUrl(dataUrl: string, fileName?: string) {
  withHistory(() => {
    const label = (fileName ? fileName.replace(/\.[^.]+$/, '') : '') || 'Изображение';
    const base = createElement(
      {
        label,
        type: 'svg',
        binding: '',
        width: 30,
        height: 30,
      },
      '',
    );
    state.currentTemplate.elements.push(
      toLabelElement({
        ...base,
        value: label,
        svgCode: buildImageSvgMarkup(dataUrl),
        svgVisible: true,
        svgPaddingTop: 0,
        svgPaddingLeft: 0,
        svgPaddingBottom: 0,
        svgPaddingRight: 0,
        svgAlign: 'left',
      } as Record<string, unknown>),
    );
    state.selectedElementId = element.id;
    state.selectedElementIds = [element.id];
  });
}

function updateElement(id: string, patch: Record<string, unknown>) {
  withHistory(() => {
    const index = state.currentTemplate.elements.findIndex((el) => el.id === id);
    if (index === -1) return;
    const prev = state.currentTemplate.elements[index];
    const next: Record<string, unknown> = { ...(prev as object), ...patch };
    next.type = prev.type;

    if (prev.type === 'datamatrix') {
      if ('datamatrixCodeSizeMm' in patch) {
        const mm = snapDataMatrixCodeSizeMm(Number(next.datamatrixCodeSizeMm));
        next.datamatrixCodeSizeMm = mm;
        next.width = mm;
        next.height = mm;
        next.datamatrixSize = 0;
      } else if ('width' in patch || 'height' in patch) {
        const side = snapDataMatrixCodeSizeMm(Math.min(Number(next.width), Number(next.height)));
        next.width = side;
        next.height = side;
        next.datamatrixCodeSizeMm = side;
        next.datamatrixSize = 0;
      }
      if ('datamatrixMode' in patch) {
        next.datamatrixManualValue = false;
        next.value =
          next.datamatrixMode === 'test'
            ? getDataMatrixTestTemplate()
            : getTemplateValueByBinding(String(next.binding), 'datamatrix') || String(next.value ?? '');
      } else if ('value' in patch) {
        next.datamatrixManualValue = true;
      }
    }

    if (prev.type === 'barcode') {
      const shouldSyncBarcodeContainerHeight =
        'barcodeHeight' in patch
        || 'barcodePaddingTop' in patch
        || 'barcodePaddingBottom' in patch
        || 'barcodeTextFontSize' in patch
        || 'barcodeTextLineHeight' in patch;
      if (shouldSyncBarcodeContainerHeight && !('height' in patch)) {
        next.height = computeBarcodeContainerHeightMm(next as unknown as BarcodeLabelElement, getEffectiveDpi());
      }
    }

    if (
      prev.type === 'svg'
      && typeof next.decorativeShapeKind === 'string'
      && 'decorativeStrokeWidth' in patch
    ) {
      const sw = clampDecorativeStrokeWidth(Number(next.decorativeStrokeWidth));
      next.decorativeStrokeWidth = sw;
      next.svgCode = buildShapeSvgMarkup(next.decorativeShapeKind as ShapeGraphicKind, sw);
    }

    state.currentTemplate.elements[index] = toLabelElement(next as unknown as Record<string, unknown>);
  });
}

function removeElement(id: string) {
  withHistory(() => {
    state.currentTemplate.elements = state.currentTemplate.elements.filter((el) => el.id !== id);
    if (state.selectedElementId === id) state.selectedElementId = null;
    state.selectedElementIds = state.selectedElementIds.filter((selectedId) => selectedId !== id);
  });
}

function setSelectedElement(id: string | null, append = false) {
  if (!id) {
    state.selectedElementId = null;
    state.selectedElementIds = [];
    return;
  }
  if (append) {
    const exists = state.selectedElementIds.includes(id);
    state.selectedElementIds = exists
      ? state.selectedElementIds.filter((item) => item !== id)
      : [...state.selectedElementIds, id];
    state.selectedElementId = state.selectedElementIds[state.selectedElementIds.length - 1] ?? null;
    return;
  }
  state.selectedElementId = id;
  state.selectedElementIds = id ? [id] : [];
}

function setSelectedElements(ids: string[]) {
  const unique = Array.from(new Set(ids)).filter((id) => state.currentTemplate.elements.some((el) => el.id === id));
  state.selectedElementIds = unique;
  state.selectedElementId = unique[unique.length - 1] ?? null;
}

function alignSelected(mode: 'left' | 'h-center' | 'right' | 'top' | 'v-center' | 'bottom') {
  withHistory(() => {
    const ids = state.selectedElementIds.length > 0
      ? state.selectedElementIds
      : (state.selectedElementId ? [state.selectedElementId] : []);
    if (ids.length === 0) return;
    const elements = state.currentTemplate.elements.filter((el) => ids.includes(el.id));
    if (elements.length === 0) return;

    const minX = Math.min(...elements.map((el) => el.x));
    const maxX = Math.max(...elements.map((el) => el.x + el.width));
    const minY = Math.min(...elements.map((el) => el.y));
    const maxY = Math.max(...elements.map((el) => el.y + el.height));
    const groupCenterX = (minX + maxX) / 2;
    const groupCenterY = (minY + maxY) / 2;
    const canvasCenterX = state.currentTemplate.labelSize.widthMm / 2;
    const canvasCenterY = state.currentTemplate.labelSize.heightMm / 2;
    const basisSingle = elements.length === 1;

    state.currentTemplate.elements = state.currentTemplate.elements.map((el) => {
      if (!ids.includes(el.id)) return el;
      if (mode === 'left') return toLabelElement({ ...(el as object), x: basisSingle ? 0 : minX } as Record<string, unknown>);
      if (mode === 'h-center') return toLabelElement({ ...(el as object), x: (basisSingle ? canvasCenterX : groupCenterX) - el.width / 2 } as Record<string, unknown>);
      if (mode === 'right') return toLabelElement({ ...(el as object), x: (basisSingle ? state.currentTemplate.labelSize.widthMm : maxX) - el.width } as Record<string, unknown>);
      if (mode === 'top') return toLabelElement({ ...(el as object), y: basisSingle ? 0 : minY } as Record<string, unknown>);
      if (mode === 'v-center') return toLabelElement({ ...(el as object), y: (basisSingle ? canvasCenterY : groupCenterY) - el.height / 2 } as Record<string, unknown>);
      return toLabelElement({ ...(el as object), y: (basisSingle ? state.currentTemplate.labelSize.heightMm : maxY) - el.height } as Record<string, unknown>);
    });
  });
}

function reorderSelected(mode: 'forward' | 'backward' | 'front' | 'back') {
  withHistory(() => {
    const ids = state.selectedElementIds.length > 0
      ? state.selectedElementIds
      : (state.selectedElementId ? [state.selectedElementId] : []);
    if (ids.length === 0) return;
    const all = [...state.currentTemplate.elements];
    const selected = all.filter((el) => ids.includes(el.id));
    const rest = all.filter((el) => !ids.includes(el.id));
    if (mode === 'front') {
      state.currentTemplate.elements = [...rest, ...selected];
      return;
    }
    if (mode === 'back') {
      state.currentTemplate.elements = [...selected, ...rest];
      return;
    }
    const direction = mode === 'forward' ? 1 : -1;
    for (const id of (direction > 0 ? [...ids].reverse() : ids)) {
      const index = all.findIndex((el) => el.id === id);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= all.length) continue;
      const tmp = all[index];
      all[index] = all[target];
      all[target] = tmp;
    }
    state.currentTemplate.elements = all;
  });
}

/**
 * Порядок в панели «Выбранные поля» = сверху вниз (как в UI после reverse).
 * insertBeforeTarget: вставить перетаскиваемый выше цели в этом списке (ближе к верху = выше по z).
 */
function reorderLayerInPanel(draggedId: string, targetId: string, insertBeforeTarget: boolean) {
  if (draggedId === targetId) return;
  withHistory(() => {
    const els = state.currentTemplate.elements;
    const vis = [...els].reverse();
    const ids = vis.map((e) => e.id);
    const from = ids.indexOf(draggedId);
    const toRef = ids.indexOf(targetId);
    if (from < 0 || toRef < 0) return;
    ids.splice(from, 1);
    let insert = ids.indexOf(targetId);
    if (insert < 0) return;
    if (!insertBeforeTarget) insert += 1;
    ids.splice(insert, 0, draggedId);
    const byId = new Map(els.map((e) => [e.id, e] as const));
    const next = ids.map((id) => byId.get(id)).filter((e): e is LabelElement => e != null);
    if (next.length !== els.length) return;
    state.currentTemplate.elements = next;
  });
}

function toggleElementHidden(id: string) {
  withHistory(() => {
    const idx = state.currentTemplate.elements.findIndex((item) => item.id === id);
    if (idx === -1) return;
    const el = state.currentTemplate.elements[idx];
    state.currentTemplate.elements[idx] = toLabelElement({
      ...(el as object),
      hidden: !el.hidden,
    } as Record<string, unknown>);
  });
}

function toggleElementLocked(id: string) {
  withHistory(() => {
    const idx = state.currentTemplate.elements.findIndex((item) => item.id === id);
    if (idx === -1) return;
    const el = state.currentTemplate.elements[idx];
    state.currentTemplate.elements[idx] = toLabelElement({
      ...(el as object),
      locked: !el.locked,
    } as Record<string, unknown>);
  });
}

function setZoom(value: number) {
  state.zoom = Math.max(0.3, Math.min(3, value));
}

function setPrintProfile(id: string) {
  if (id === 'custom') {
    state.printProfileId = 'custom';
    syncTemplateDpi();
    return;
  }
  const profile = PRINT_PROFILES.find((item) => item.id === id);
  if (!profile) return;
  state.printProfileId = profile.id;
  state.safeMarginMm = profile.safeMarginMm;
  state.printDensity = profile.density;
  state.lastPrintDpi = profile.dpi;
  syncTemplateDpi();
}

function setPrintDensity(value: number) {
  state.printDensity = Math.max(0, Math.min(10, Math.round(value)));
  const profile = PRINT_PROFILES.find((item) => item.id === state.printProfileId);
  if (!profile || profile.density !== state.printDensity) {
    state.printProfileId = 'custom';
  }
}

function setSafeMarginMm(value: number) {
  state.safeMarginMm = Math.max(0, value);
  const profile = PRINT_PROFILES.find((item) => item.id === state.printProfileId);
  if (!profile || Math.abs(profile.safeMarginMm - state.safeMarginMm) > 1e-6) {
    state.printProfileId = 'custom';
  }
}

function setSafeMarginEnabled(value: boolean) {
  state.safeMarginEnabled = value;
}

function setShowGrid(value: boolean) {
  state.showGrid = value;
}

function setCanvasMirrored(value: boolean) {
  state.canvasMirrored = value;
}

function setSnap(enabled: boolean) {
  state.snapEnabled = enabled;
}

function setSnapStepMm(step: number) {
  state.snapStepMm = Math.max(0.1, step);
}

function setPreviewMode(value: boolean) {
  state.previewMode = value;
}

function setActiveFieldTab(key: DesignerFieldTabKey) {
  state.activeFieldTab = key;
}

function syncElementValues() {
  state.currentTemplate.elements = state.currentTemplate.elements.map((element) => {
    if (element.type === 'datamatrix') {
      if (element.datamatrixMode === 'test') {
        return mergeElementDefaults({
          ...element,
          value: getDataMatrixTestTemplate(),
        });
      }
      if (element.datamatrixManualValue) return mergeElementDefaults(element);
      return mergeElementDefaults({
        ...element,
        value: '{{gtin}}{{serial}}',
      });
    }
    return mergeElementDefaults(element);
  });
}

function setSelectedProduct(id: string) {
  withHistory(() => {
    const product = state.products.find((item) => item.id === id);
    if (!product) return;
    const firstSize = product.sizes[0] ?? null;
    state.selectedProductId = product.id;
    state.selectedProduct = product;
    state.selectedSize = firstSize;
    state.selectedSizeBarcode = firstSize?.barcode ?? '';
    refreshPreviewData();
  });
}

function setSelectedSize(barcode: string) {
  withHistory(() => {
    const size = state.selectedProduct?.sizes.find((item) => item.barcode === barcode);
    if (!size) return;
    state.selectedSizeBarcode = size.barcode;
    state.selectedSize = size;
    refreshPreviewData();
  });
}

function setLabelContext(ctx: LabelDesignerContext) {
  withHistory(() => {
    state.labelContext = ctx;
    refreshPreviewData();
  });
  if (ctx === 'warehouse') void loadCellPreviewData();
}

function setSelectedCellWarehouse(warehouseId: string) {
  withHistory(() => {
    state.selectedCellWarehouseId = warehouseId;
    const forWh = state.cellPreviewUnitsAll.filter((u) => u.warehouseId === warehouseId);
    const nextUnit =
      forWh.find((u) => u.id === state.selectedCellUnitId) ?? forWh[0] ?? null;
    state.selectedCellUnitId = nextUnit?.id ?? null;
    refreshPreviewData();
  });
}

function setSelectedCellStorageUnit(unitId: string) {
  withHistory(() => {
    state.selectedCellUnitId = unitId;
    refreshPreviewData();
  });
}

function refreshDataMatrixTestValues() {
  syncElementValues();
}

function saveTemplateJson(): string {
  syncTemplateDpi();
  return JSON.stringify(state.currentTemplate);
}

function isLabelTemplateJson(parsed: unknown): parsed is LabelTemplate {
  if (!parsed || typeof parsed !== 'object') return false;
  const o = parsed as LabelTemplate;
  if (!Array.isArray(o.elements) || !o.labelSize || typeof o.labelSize !== 'object') return false;
  const { widthMm, heightMm } = o.labelSize;
  return Number.isFinite(Number(widthMm)) && Number.isFinite(Number(heightMm)) && Number(widthMm) > 0 && Number(heightMm) > 0;
}

function parseTemplateJsonSource(raw: unknown): unknown {
  if (typeof raw === 'string') return JSON.parse(raw);
  return raw;
}

function createNewTemplate(size?: { widthMm: number; heightMm: number }) {
  withHistory(() => {
    const base = createDefaultTemplate();
    const widthMm = size?.widthMm ?? base.labelSize.widthMm;
    const heightMm = size?.heightMm ?? base.labelSize.heightMm;
    state.currentTemplate = {
      ...base,
      id: `template_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
      labelSize: { widthMm, heightMm },
      name: `Этикетка ${widthMm}x${heightMm}`,
      dpi: getEffectiveDpi(),
    };
    state.activeTemplateId = null;
    state.selectedElementId = null;
    state.selectedElementIds = [];
    syncElementValues();
  });
}

function setTemplateName(name: string) {
  state.currentTemplate.name = name;
}

function setTemplateDescription(description: string) {
  state.currentTemplate.description = description;
}

function loadTemplateJson(raw: unknown): boolean {
  try {
    const parsed = parseTemplateJsonSource(raw);
    if (!isLabelTemplateJson(parsed)) throw new Error('Invalid template');
    withHistory(() => {
      const template: LabelTemplate = {
        ...parsed,
        paperType: parsed.paperType === 'a4' ? 'a4' : 'thermal',
        elements: parsed.elements.map((element) =>
          mergeElementDefaults({
            ...element,
            value: element.value ?? '',
          }),
        ),
      };
      state.currentTemplate = template;
      if (parsed.dpi === 203 || parsed.dpi === 300) {
        applyTemplateDpi(parsed.dpi);
      } else {
        syncTemplateDpi();
      }
      syncElementValues();
      state.selectedElementId = null;
      state.selectedElementIds = [];
    });
    return true;
  } catch (e) {
    console.error('copyTemplateError', e);
    return false;
  }
}

function validateTemplateVariables(): string[] {
  const warnings: string[] = [];
  state.currentTemplate.elements.forEach((element) => {
    const unknown = validateFastwmsTemplate(element.value || '');
    unknown.forEach((name) => warnings.push(`${element.label}: ${name}`));
  });
  return warnings;
}

function persistState() {
  if (typeof window === 'undefined') return;
  const payload = {
    currentTemplate: state.currentTemplate,
    zoom: state.zoom,
    printProfileId: state.printProfileId,
    printDensity: state.printDensity,
    lastPrintDpi: state.lastPrintDpi,
    safeMarginMm: state.safeMarginMm,
    safeMarginEnabled: state.safeMarginEnabled,
    canvasMirrored: state.canvasMirrored,
    showGrid: state.showGrid,
    snapEnabled: state.snapEnabled,
    snapStepMm: state.snapStepMm,
    selectedProductId: state.selectedProductId,
    selectedSizeBarcode: state.selectedSizeBarcode,
    activeFieldTab: state.activeFieldTab,
    labelContext: state.labelContext,
    selectedCellWarehouseId: state.selectedCellWarehouseId,
    selectedCellUnitId: state.selectedCellUnitId,
    activeTemplateId: state.activeTemplateId,
  };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

function restoreState() {
  if (typeof window === 'undefined') return;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return;
  try {
    isTimeTraveling = true;
    const parsed = JSON.parse(raw) as {
      currentTemplate?: LabelTemplate;
      zoom?: number;
      dpi?: 203 | 300;
      printProfileId?: string;
      printDensity?: number;
      lastPrintDpi?: 203 | 300;
      safeMarginMm?: number;
      safeMarginEnabled?: boolean;
      canvasMirrored?: boolean;
      showGrid?: boolean;
      snapEnabled?: boolean;
      snapStepMm?: number;
      selectedProductId?: string;
      selectedSizeBarcode?: string;
      activeFieldTab?: DesignerFieldTabKey;
      labelContext?: LabelDesignerContext;
      selectedCellWarehouseId?: string | null;
      selectedCellUnitId?: string | null;
      activeTemplateId?: string | null;
    };
    if (parsed.activeTemplateId?.trim()) {
      state.activeTemplateId = parsed.activeTemplateId.trim();
      if (
        parsed.currentTemplate
        && Array.isArray(parsed.currentTemplate.elements)
        && parsed.currentTemplate.labelSize
      ) {
        state.currentTemplate = {
          ...parsed.currentTemplate,
          elements: parsed.currentTemplate.elements.map((element) => mergeElementDefaults(element)),
        };
        if (parsed.currentTemplate.dpi === 203 || parsed.currentTemplate.dpi === 300) {
          applyTemplateDpi(parsed.currentTemplate.dpi);
        } else {
          syncTemplateDpi();
        }
      }
    } else {
      state.activeTemplateId = null;
      state.currentTemplate = {
        ...createDefaultTemplate(),
        id: `template_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
        elements: [],
      };
      syncTemplateDpi();
    }
    if (typeof parsed.zoom === 'number') setZoom(parsed.zoom);
    if (parsed.lastPrintDpi === 203 || parsed.lastPrintDpi === 300) {
      state.lastPrintDpi = parsed.lastPrintDpi;
    }
    if (typeof parsed.printDensity === 'number') {
      state.printDensity = Math.max(0, Math.min(10, Math.round(parsed.printDensity)));
    }
    if (typeof parsed.printProfileId === 'string') {
      if (parsed.printProfileId === 'custom') {
        state.printProfileId = 'custom';
        if (typeof parsed.safeMarginMm === 'number') state.safeMarginMm = Math.max(0, parsed.safeMarginMm);
      } else if (PRINT_PROFILES.some((p) => p.id === parsed.printProfileId)) {
        setPrintProfile(parsed.printProfileId);
      }
    } else if (parsed.dpi === 203 || parsed.dpi === 300) {
      state.lastPrintDpi = parsed.dpi;
      const presetId = parsed.dpi === 300 ? 'thermal-300' : 'thermal-203';
      setPrintProfile(presetId);
      if (typeof parsed.safeMarginMm === 'number' && Math.abs(parsed.safeMarginMm - state.safeMarginMm) > 1e-6) {
        state.printProfileId = 'custom';
        state.safeMarginMm = Math.max(0, parsed.safeMarginMm);
      }
    } else if (typeof parsed.safeMarginMm === 'number') {
      setSafeMarginMm(parsed.safeMarginMm);
    }
    if (typeof parsed.safeMarginEnabled === 'boolean') setSafeMarginEnabled(parsed.safeMarginEnabled);
    if (typeof parsed.canvasMirrored === 'boolean') setCanvasMirrored(parsed.canvasMirrored);
    if (typeof parsed.showGrid === 'boolean') setShowGrid(parsed.showGrid);
    if (typeof parsed.snapEnabled === 'boolean') setSnap(parsed.snapEnabled);
    if (typeof parsed.snapStepMm === 'number') setSnapStepMm(parsed.snapStepMm);
    if (parsed.labelContext === 'warehouse' || parsed.labelContext === 'product') {
      state.labelContext = parsed.labelContext;
    }
    if (parsed.selectedProductId) {
      state.selectedProductId = parsed.selectedProductId;
    }
    if (parsed.selectedSizeBarcode) {
      state.selectedSizeBarcode = parsed.selectedSizeBarcode;
    }
    if (parsed.activeFieldTab === 'product' || parsed.activeFieldTab === 'codes' || parsed.activeFieldTab === 'signs') {
      state.activeFieldTab = parsed.activeFieldTab;
    }
    if (typeof parsed.selectedCellWarehouseId === 'string' || parsed.selectedCellWarehouseId === null) {
      state.selectedCellWarehouseId = parsed.selectedCellWarehouseId ?? null;
    }
    if (typeof parsed.selectedCellUnitId === 'string' || parsed.selectedCellUnitId === null) {
      state.selectedCellUnitId = parsed.selectedCellUnitId ?? null;
    }
  } catch {
    // ignore broken storage and continue with defaults
  } finally {
    isTimeTraveling = false;
  }
}

restoreState();

export function mapApiProductSize(raw: Record<string, unknown>): ProductSizeVariant {
  const value = String(raw.value ?? '').trim();
  const tech = String(raw.tech_size ?? raw.techSize ?? '').trim();
  return {
    barcode: String(raw.barcode ?? ''),
    value: value,
    techSize: tech,
    size: tech,
  };
}

export function mapWbToDesignerProduct(p: WbProduct): DesignerProduct {
  const client = p.client;
  const rawSizes = Array.isArray(p.sizes) ? p.sizes : [];
  return {
    id: String(p.id),
    templateId: undefined,
    photoUrl: p.main_image_url ?? '',
    name: p.name,
    sellerName: client?.name ?? '',
    sellerShortName: client?.short_name ?? client?.name ?? '',
    shortAddress: client?.short_address ?? '',
    address: client?.address ?? '',
    color: p.color,
    article: p.article,
    sellerArticle: p.vendor_code,
    brand: p.brand?.name ?? '',
    needsMarking: p.has_chestny_znak,
    category: p.wb_category?.name ?? '',
    productType: p.wb_category?.name ?? '',
    composition: p.composition,
    sizes: rawSizes.map((item) =>
      mapApiProductSize(typeof item === 'object' && item != null ? (item as Record<string, unknown>) : {}),
    ),
  };
}

function applyProductsFromApi(list: DesignerProduct[]) {
  state.products = list;
  if (!list.length) {
    state.selectedProductId = '';
    state.selectedSizeBarcode = '';
    state.selectedProduct = null;
    state.selectedSize = null;
    refreshPreviewData();
    return;
  }
  const curId = state.selectedProductId;
  if (!curId) {
    refreshPreviewData();
    return;
  }
  const next = list.find((p) => p.id === curId);
  if (!next) {
    state.selectedProductId = '';
    state.selectedSizeBarcode = '';
    state.selectedProduct = null;
    state.selectedSize = null;
    refreshPreviewData();
    return;
  }
  const nextSize = next.sizes.find((s) => s.barcode === state.selectedSizeBarcode) ?? null;
  state.selectedProductId = next.id;
  state.selectedProduct = next;
  state.selectedSize = nextSize;
  state.selectedSizeBarcode = nextSize?.barcode ?? '';
  refreshPreviewData();
}

async function loadProductsFromServer() {
  const payload: FilterRequest = {
    filters: [],
    sort_by: 'name',
    sort_dir: 'asc',
  };

  const { data, error } = await getProductsWithSizes(payload);

  if (error.value) {
    console.error('Ошибка загрузки товаров:', error.value);
    return;
  }

  const list = (data.value?.data ?? []) as WbProduct[];
  const mapped = list.map(mapWbToDesignerProduct);
  applyProductsFromApi(mapped);
}

watch(
  () => [
    state.currentTemplate,
    state.zoom,
    state.printProfileId,
    state.printDensity,
    state.lastPrintDpi,
    state.safeMarginMm,
    state.safeMarginEnabled,
    state.canvasMirrored,
    state.showGrid,
    state.snapEnabled,
    state.snapStepMm,
    state.selectedProductId,
    state.selectedSizeBarcode,
    state.activeFieldTab,
    state.labelContext,
    state.selectedCellWarehouseId,
    state.selectedCellUnitId,
    state.activeTemplateId,
  ],
  () => {
    persistState();
  },
  { deep: true },
);

export function useDesignerStore() {
  return {
    state,
    effectiveDpi,
    cellPreviewUnitsForSelectedWarehouse,
    getPrintDpi: getEffectiveDpi,
    selectedElement,
    selectedElements,
    setPaperType,
    setLabelSize,
    addElement,
    addStaticText,
    updateElement,
    removeElement,
    setSelectedElement,
    setSelectedElements,
    alignSelected,
    reorderSelected,
    reorderLayerInPanel,
    toggleElementHidden,
    toggleElementLocked,
    setZoom,
    setPrintProfile,
    setPrintDensity,
    setSafeMarginMm,
    setSafeMarginEnabled,
    setCanvasMirrored,
    setShowGrid,
    setSnap,
    setSnapStepMm,
    setPreviewMode,
    setActiveFieldTab,
    canUndo,
    canRedo,
    undo,
    redo,
    setSelectedProduct,
    setSelectedSize,
    setLabelContext,
    setSelectedCellWarehouse,
    setSelectedCellStorageUnit,
    loadCellPreviewData,
    loadProductsFromServer,
    setPreviewTestEnabled,
    setPreviewTestOverrides,
    fillPreviewTestDefaults,
    resetPreviewTestOverrides,
    refreshDataMatrixTestValues,
    addCatalogElement,
    addShapeGraphic,
    addImageFromDataUrl,
    saveTemplateJson,
    loadTemplateJson,
    createNewTemplate,
    setTemplateName,
    setTemplateDescription,
    validateTemplateVariables,
  };
}
